import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Map, OrderedMap } from 'immutable';
import cx from 'classnames';

import { ExperimentsConsumer } from 'providers/experiments-provider';

import StreetParkingControls from 'containers/search/street-parking-controls';

import LoadingPulse from 'components/common/loading-pulse';
import ToggleBar from 'components/map/toggle-bar';
import LocationMarker from 'components/search/map/markers/location';
import DestinationMarker from 'components/search/map/markers/destination';
import GoogleMarkerWrapper from 'components/search/map/markers/google-wrapper';
import GoogleMapView from 'components/search/map/google-map-view';

import Search from 'models/search';
import { Location } from 'models/locations';
import Quote from 'models/quote';

import highlightLocation from 'action-creators/search/highlight-location';
import changeSelectedLocation from 'action-creators/search/change-selected-location';
import boundsChange from 'action-creators/search/bounds-change';
import updateCurrentSearch from 'action-creators/search/update-current-search';
import toggleMap from 'action-creators/search/toggle-map';
import parkingNearMeSearch from 'action-creators/search/parking-near-me-search';
import setCurrentSearchAndChangeBounds from 'action-creators/search/set-current-search-and-change-bounds';
import trackEventCreator from 'action-creators/analytics/track-event';
import scrollToLocation from 'action-creators/search/scroll-to-location';
import addMessage from 'action-creators/messaging/add-message';

import { usesLargeFormatLocationDetails } from 'lib/common/search-helpers';

const propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  previousSearch: PropTypes.instanceOf(Search).isRequired,
  locations: PropTypes.instanceOf(OrderedMap).isRequired,
  events: PropTypes.instanceOf(OrderedMap).isRequired,
  streetParking: PropTypes.instanceOf(Map).isRequired,
  showStreetParking: PropTypes.bool.isRequired,
  limitEventZoomToFirstTen: PropTypes.bool,
  clientSettings: PropTypes.instanceOf(Map),

  highlightedLocation: PropTypes.instanceOf(Location),
  selectedLocation: PropTypes.instanceOf(Location),
  selectedQuote: PropTypes.instanceOf(Quote),
  airport: PropTypes.bool,

  updateCurrentSearch: PropTypes.func.isRequired,
  changeSelectedLocation: PropTypes.func.isRequired,
  boundsChange: PropTypes.func.isRequired,
  highlightLocation: PropTypes.func.isRequired,
  toggleMap: PropTypes.func.isRequired,
  scrollToLocation: PropTypes.func.isRequired,
  addMessage: PropTypes.func.isRequired,
};

const defaultProps = {
  highlightedLocation: null,
  selectedLocation: null,
  selectedQuote: null,
  airport: false,
  forceRefresh: false,
  limitEventZoomToFirstTen: false,
  clientSettings: Map(),
};

export class SearchMap extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const { currentSearch, selectedLocation } = nextProps;
    const { destination } = currentSearch;
    let { lat, lng } = currentSearch;

    // Re-center map if destination changes, or if location selected
    if (destination !== prevState.destination || selectedLocation) {
      ({ anchorLat: lat, anchorLng: lng } = currentSearch);
    }

    const recenter = destination !== prevState.destination &&
                    !(prevState.selectedLocation && !nextProps.selectedLocation);

    return {
      ...prevState,
      destination,
      selectedLocation,
      lat,
      lng,
      recenter,
      currentSearch,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  markerPriceForLocation(location) {
    if (!location) { return 0; }
    const { airport, clientSettings, currentSearch, selectedQuote } = this.props;

    let price = 0;

    if (airport) {
      const isReservation = currentSearch.isReservationSearch;
      const key = isReservation ? 'reservationDailyMin' : 'driveUpDailyMin';
      price = clientSettings.get(key);
    } else {
      const quote = selectedQuote || location.getQuote();

      if (quote) {
        price = quote.price;
      }
    }

    return price;
  }

  get locationMarkers() {
    const { highlightedLocation, selectedLocation } = this.props;
    let { locations } = this.props;
    locations = locations.sortBy(l => l.entrances.first().lat).toArray();

    return locations.map((location, i) => (
      <GoogleMarkerWrapper
        key={`lm-${location.id}`}
        position={location.entrances.first()}
        zIndex={LocationMarker.zIndex({
          location,
          selectedLocation,
          highlightedLocation,
          latRank: i,
          locationCount: locations.length,
        })}
      >
        <LocationMarker
          location={location}
          price={this.markerPriceForLocation(location)}
          selectedLocation={selectedLocation}
          highlightedLocation={highlightedLocation}
          changeSelectedLocation={this.props.changeSelectedLocation}
          scrollToLocation={this.props.scrollToLocation}
          highlightLocation={this.props.highlightLocation}
          toggleMap={this.props.toggleMap}
          latRank={i}
          locationCount={locations.length}
        />
      </GoogleMarkerWrapper>
    ));
  }

  get destinationCoordinates() {
    const { currentSearch } = this.props;
    return { lat: currentSearch.anchorLat, lng: currentSearch.anchorLng };
  }

  render() {
    const { currentSearch, selectedLocation, locations } = this.props;
    const mapClasses = cx({
      'airport-map': usesLargeFormatLocationDetails(this.props),
    });

    const wrapperClasses = cx({
      'map-wrapper': true,
      'airport-map-wrapper': usesLargeFormatLocationDetails(this.props),
      'visible-xs': usesLargeFormatLocationDetails(this.props) && selectedLocation,
      'visible-sm': usesLargeFormatLocationDetails(this.props) && selectedLocation,
    });

    return (
      <div className={wrapperClasses}>
        <ToggleBar
          selectedLocation={this.props.selectedLocation}
          events={this.props.events}
          currentSearch={currentSearch}
          changeSelectedLocation={this.props.changeSelectedLocation}
          toggleMap={this.props.toggleMap}
        />
        <LoadingPulse visible={this.state.loading} />
        <StreetParkingControls />
          <div className={mapClasses} id="map">
            <GoogleMapView
              currentSearch={currentSearch}
              selectedLocation={selectedLocation}
              locations={locations}
              boundsChange={this.props.boundsChange}
              streetParking={this.props.streetParking}
              showStreetParking={this.props.showStreetParking}
              limitEventZoomToFirstTen={this.props.limitEventZoomToFirstTen}
            >
              {this.destinationCoordinates && (
                <GoogleMarkerWrapper position={this.destinationCoordinates} zIndex={0}>
                  <DestinationMarker />
                </GoogleMarkerWrapper>
              )}
            {this.locationMarkers}
          </GoogleMapView>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    changeSelectedLocation,
    highlightLocation,
    boundsChange,
    updateCurrentSearch,
    parkingNearMeSearch,
    toggleMap,
    setCurrentSearchAndChangeBounds,
    trackEvent: trackEventCreator,
    scrollToLocation,
    addMessage,
  }, dispatch)
);

const mapStateToProps = (state, ownProps) => ({
  locations: state.search.locations,
  clientSettings: state.app.clientSettings,
  currentSearch: state.search.currentSearch,
  previousSearch: state.search.previousSearch,
  highlightedLocation: state.search.highlightedLocation,
  forceMapRefresh: state.search.forceMapRefresh,
  insights: state.analytics.insights,
  event: state.search.event,
  events: state.search.events,
  selectedLocation: state.search.selectedLocation,
  selectedQuote: state.search.selectedQuote,
  streetParking: state.search.streetParking,
  showStreetParking: state.search.showStreetParking,
  airport: ownProps.airport,
});

const SearchMapWrapper = props => (
  <ExperimentsConsumer>
    <SearchMap {...props} />
  </ExperimentsConsumer>
);

SearchMap.propTypes = propTypes;
SearchMap.defaultProps = defaultProps;
export default connect(mapStateToProps, mapDispatchToProps)(SearchMapWrapper);
